import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ToastService } from '../../../services/toast.service';
import { LoadingService } from '../../../services/loading.service';
import { BankReconciliationService, ReconciliationHistoryParams } from '../../../services/bank-reconciliation.service';
import { BankClosingService, BankClosingRequestDTO, BankClosingSummaryDTO, PeriodDetailsResponseDTO } from '../../../services/bank-closing.service';
import { BanksService } from '../../../services/banks.service';
import { forkJoin } from 'rxjs';

@Component({
  selector: 'app-bank-reconciliation-list',
  templateUrl: './bank-reconciliation-list.component.html',
  styleUrl: './bank-reconciliation-list.component.scss'
})
export class BankReconciliationListComponent implements OnInit {

  // === PROPIEDADES DE CIERRE DE PERIODO ===
  showClosingModal: boolean = false;
  closingData = {
    bankId: null as number | null,
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear()
  };
  preClosingSummary: BankClosingSummaryDTO | null = null;
  loadingSummary: boolean = false;
  confirmationCheck: boolean = false;
  errorMessage: string | null = null;

  // Wizard state
  currentStep: number = 1;
  totalSteps: number = 3;
  periodDetails: PeriodDetailsResponseDTO | null = null;
  loadingVouchers: boolean = false;

  months = [
    { label: 'Enero', value: 1 }, { label: 'Febrero', value: 2 },
    { label: 'Marzo', value: 3 }, { label: 'Abril', value: 4 },
    { label: 'Mayo', value: 5 }, { label: 'Junio', value: 6 },
    { label: 'Julio', value: 7 }, { label: 'Agosto', value: 8 },
    { label: 'Septiembre', value: 9 }, { label: 'Octubre', value: 10 },
    { label: 'Noviembre', value: 11 }, { label: 'Diciembre', value: 12 }
  ];

  // ... (existing constructor)

  // ... (existing ngOnInit/loadInitialData)

  // === CIERRE DE PERIODO ===

  openClosingModal(): void {
    this.closingData = {
      bankId: null,
      month: new Date().getMonth(), // Mes anterior por defecto? O actual
      year: new Date().getFullYear()
    };
    // Ajuste mes anterior
    if (this.closingData.month === 0) {
      this.closingData.month = 12;
      this.closingData.year--;
    }

    this.preClosingSummary = null;
    this.confirmationCheck = false;
    this.showClosingModal = true;
  }

  onClosingDataChange(): void {
    this.confirmationCheck = false;
    if (this.closingData.bankId && this.closingData.month && this.closingData.year) {
      this.fetchPreClosingSummary();
    } else {
      this.preClosingSummary = null;
    }
  }

  fetchPreClosingSummary(): void {
    if (!this.closingData.bankId) return;

    this.loadingSummary = true;
    this.errorMessage = null;
    this.bankClosingService.getPreClosingSummary(this.closingData.bankId, this.closingData.month, this.closingData.year)
      .subscribe({
        next: (response) => {
          this.loadingSummary = false;
          if (response.isSuccess && response.data) {
            this.preClosingSummary = response.data;
            this.errorMessage = null;
          } else {
            this.preClosingSummary = null;
            this.errorMessage = response.errorMessage || 'No se pudo obtener el resumen o el periodo no es válido.';
          }
        },
        error: (error) => {
          this.loadingSummary = false;
          this.preClosingSummary = null;
          this.errorMessage = 'Error de conexión al obtener el resumen.';
          console.error('Error getting summary', error);
        }
      });
  }

  submitClosing(): void {
    if (!this.closingData.bankId || !this.closingData.month || !this.closingData.year) {
      this.toastService.showToast('warning', 'Todos los campos son obligatorios');
      return;
    }

    // Ya no usamos confirm(), la validación es visual y por checkbox
    this.loadingService.show();

    const dto: BankClosingRequestDTO = {
      bankId: this.closingData.bankId,
      month: this.closingData.month,
      year: this.closingData.year
    };

    this.bankClosingService.closePeriod(dto).subscribe({
      next: (response) => {
        this.loadingService.hide();
        if (response.isSuccess) {
          this.toastService.showToast('success', 'Periodo cerrado exitosamente');
          this.closeClosingModal();
          // Opcional: Recargar o mostrar resumen
        } else {
          this.toastService.showToast('error', response.errorMessage || 'Error al cerrar el periodo');
        }
      },
      error: (error) => {
        this.loadingService.hide();
        console.error('Error closing period:', error);
        this.toastService.showToast('error', 'Error al procesar el cierre');
      }
    });
  }

  // Wizard navigation methods
  nextStep(): void {
    if (this.currentStep === 1 && this.closingData.bankId) {
      // Load vouchers before going to step 2
      this.loadPeriodDetails();
    } else if (this.currentStep === 2) {
      // Load summary before going to step 3
      this.fetchPreClosingSummary();
    }

    if (this.currentStep < this.totalSteps) {
      this.currentStep++;
    }
  }

  previousStep(): void {
    if (this.currentStep > 1) {
      this.currentStep--;
    }
  }

  canProceedToNextStep(): boolean {
    if (this.currentStep === 1) {
      return this.closingData.bankId !== null;
    }
    if (this.currentStep === 2) {
      return !this.loadingVouchers && this.periodDetails !== null;
    }
    if (this.currentStep === 3) {
      return this.confirmationCheck && this.preClosingSummary?.canClose === true;
    }
    return false;
  }

  loadPeriodDetails(): void {
    if (!this.closingData.bankId) return;

    this.loadingVouchers = true;
    this.errorMessage = null;
    this.bankClosingService.getPeriodDetails(this.closingData.bankId, this.closingData.month, this.closingData.year)
      .subscribe({
        next: (response) => {
          this.loadingVouchers = false;
          if (response.isSuccess && response.data) {
            this.periodDetails = response.data;
            this.errorMessage = null;
          } else {
            this.periodDetails = null;
            this.errorMessage = response.errorMessage || 'No se pudieron cargar los detalles del período.';
          }
        },
        error: (error) => {
          this.loadingVouchers = false;
          this.periodDetails = null;
          this.errorMessage = 'Error de conexión al cargar los detalles del período.';
          console.error('Error loading period details', error);
        }
      });
  }

  openVoucher(voucherType: string, voucherId: number): void {
    const route = voucherType === 'INCOME' ? '/income-receipts/view' : '/expense-receipts/view';
    const url = this.router.serializeUrl(
      this.router.createUrlTree([route, voucherId])
    );
    window.open(url, '_blank');
  }

  openReconciliation(reconciliationId: number): void {
    const url = this.router.serializeUrl(
      this.router.createUrlTree(['/reconciliation', reconciliationId])
    );
    window.open(url, '_blank');
  }

  closeClosingModal(): void {
    this.showClosingModal = false;
    this.currentStep = 1; // Reset wizard
    this.periodDetails = null;
    this.preClosingSummary = null;
    this.confirmationCheck = false;
    this.errorMessage = null;
  }

  // ... (existing methods)

  reconciliations: any[] = [];
  banks: any[] = [];

  // === PROPIEDADES DE FILTROS ===
  filters = {
    bankId: null as number | null,
    status: null as string | null,
    searchTerm: ''
  };

  // === PROPIEDADES DE PAGINACIÓN ===
  currentPage: number = 1;
  pageSize: number = 20;
  totalRecords: number = 0;
  totalPages: number = 0;

  // === PROPIEDADES DE ESTADO ===
  loading: boolean = false;
  showFilters: boolean = false;

  // === OPCIONES DE ESTADO ===
  statusOptions = [
    { label: 'Todos los Estados', value: null },
    { label: 'En Progreso', value: 'IN_PROGRESS' },
    { label: 'Completada', value: 'COMPLETED' },
    { label: 'Cancelada', value: 'CANCELLED' }
  ];

  constructor(
    private router: Router,
    private toastService: ToastService,
    private loadingService: LoadingService,
    private bankReconciliationService: BankReconciliationService,
    private bankClosingService: BankClosingService,
    private banksService: BanksService
  ) { }

  ngOnInit(): void {
    this.loadInitialData();
  }

  // === CARGA DE DATOS ===

  loadInitialData(): void {
    this.loadingService.show();
    forkJoin({
      banks: this.banksService.getAll(),
      reconciliations: this.loadReconciliations()
    }).subscribe({
      next: (results) => {
        // Manejar estructura paginada: data puede ser array directo o {data: [...], totalCount: ...}
        const banksData = Array.isArray(results.banks.data) ? results.banks.data : (results.banks.data?.data || []);
        
        // Mapear bancos
        this.banks = [
          { label: 'Todos los Bancos', value: null },
          ...banksData.map((bank: any) => ({
            label: `${bank.bankName} - ${bank.accountNumber}`,
            value: bank.id,
            data: bank
          }))
        ];

        this.loadingService.hide();
      },
      error: (error) => {
        console.error('Error loading initial data:', error);
        this.toastService.showToast('error', 'Error cargando datos iniciales');
        this.loadingService.hide();
      }
    });
  }

  loadReconciliations(): Promise<any> {
    return new Promise((resolve, reject) => {
      const params: ReconciliationHistoryParams = {
        bankId: this.filters.bankId || undefined,
        status: this.filters.status || undefined,
        pageNumber: this.currentPage,
        pageSize: this.pageSize
      };

      this.bankReconciliationService.getReconciliationHistory(params).subscribe({
        next: (response) => {
          if (response?.isSuccess) {
            this.reconciliations = response.data?.data || [];
            this.totalRecords = response.data?.totalCount || 0;
            this.totalPages = Math.ceil(this.totalRecords / this.pageSize);
            resolve(response);
          } else {
            this.toastService.showToast('error', response?.errorMessage || 'Error cargando conciliaciones');
            reject(response?.errorMessage);
          }
        },
        error: (error) => {
          console.error('Error loading reconciliations:', error);
          this.toastService.showToast('error', 'Error cargando conciliaciones');
          reject(error);
        }
      });
    });
  }

  // === FILTROS Y BÚSQUEDA ===

  onFilterChange(): void {
    this.currentPage = 1; // Resetear a primera página
    this.loadReconciliations();
  }

  clearFilters(): void {
    this.filters = {
      bankId: null,
      status: null,
      searchTerm: ''
    };
    this.onFilterChange();
    this.toastService.showToast('success', 'Filtros limpiados');
  }

  toggleFilters(): void {
    this.showFilters = !this.showFilters;
  }

  // === PAGINACIÓN ===

  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.loadReconciliations();
    }
  }

  previousPage(): void {
    if (this.currentPage > 1) {
      this.goToPage(this.currentPage - 1);
    }
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.goToPage(this.currentPage + 1);
    }
  }

  // === ACCIONES ===

  createNewReconciliation(): void {
    this.router.navigate(['/bank-reconciliation/new']);
  }

  viewReconciliation(reconciliation: any): void {
    this.router.navigate(['/bank-reconciliation', reconciliation.id]);
  }

  async downloadReconciliationReport(reconciliation: any): Promise<void> {
    if (reconciliation.status !== 'COMPLETED') {
      this.toastService.showToast('warning', 'Solo se pueden generar reportes de conciliaciones completadas');
      return;
    }

    this.loadingService.show();

    try {
      const blob = await this.bankReconciliationService.generateReconciliationReport(reconciliation.id).toPromise();
      if (blob) {
        const filename = `ConciliacionBancaria_${reconciliation.id}_${new Date().toISOString().slice(0, 10)}.xlsx`;
        this.bankReconciliationService.downloadReport(blob, filename);
        this.toastService.showToast('success', 'Reporte descargado exitosamente');
      } else {
        this.toastService.showToast('error', 'No se pudo generar el reporte');
      }

    } catch (error) {
      console.error('Error downloading report:', error);
      this.toastService.showToast('error', 'Error descargando reporte');
    } finally {
      this.loadingService.hide();
    }
  }

  async cancelReconciliation(reconciliation: any): Promise<void> {
    if (reconciliation.status !== 'IN_PROGRESS') {
      this.toastService.showToast('warning', 'Solo se pueden cancelar conciliaciones en progreso');
      return;
    }

    const reason = prompt('Ingrese la razón de la cancelación:');
    if (!reason || reason.trim() === '') {
      this.toastService.showToast('warning', 'La razón de cancelación es obligatoria');
      return;
    }

    this.loadingService.show();

    try {
      const response = await this.bankReconciliationService.cancelReconciliation(reconciliation.id, reason.trim()).toPromise();

      if (response?.isSuccess) {
        this.toastService.showToast('success', 'Conciliación cancelada exitosamente');
        await this.loadReconciliations(); // Recargar lista
      } else {
        this.toastService.showToast('error', response?.errorMessage || 'Error cancelando conciliación');
      }

    } catch (error) {
      console.error('Error canceling reconciliation:', error);
      this.toastService.showToast('error', 'Error cancelando conciliación');
    } finally {
      this.loadingService.hide();
    }
  }

  // === MÉTODOS DE DISPLAY ===

  getStatusDisplayName(status: string): string {
    switch (status) {
      case 'IN_PROGRESS':
        return 'En Progreso';
      case 'COMPLETED':
        return 'Completada';
      case 'CANCELLED':
        return 'Cancelada';
      default:
        return status;
    }
  }

  getStatusBadgeClass(status: string): string {
    switch (status) {
      case 'IN_PROGRESS':
        return 'bg-yellow-100 text-yellow-800';
      case 'COMPLETED':
        return 'bg-green-100 text-green-800';
      case 'CANCELLED':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  }

  getReconciliationTypeDisplayName(type: string): string {
    switch (type) {
      case 'INCOME':
        return 'Ingresos';
      case 'EXPENSE':
        return 'Egresos';
      case 'BOTH':
        return 'Completa';
      default:
        return type;
    }
  }

  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  }

  formatDate(date: string): string {
    return new Date(date).toLocaleDateString('es-CO');
  }

  getBankName(reconciliation: any): string {
    return reconciliation.bank?.displayName || 'Banco no especificado';
  }

  getProgressPercentage(reconciliation: any): number {
    return reconciliation.progressPercentage || 0;
  }

  isBalanced(reconciliation: any): boolean {
    return reconciliation.isBalanced || false;
  }

  // === MÉTODOS DE VALIDACIÓN ===

  canDownloadReport(reconciliation: any): boolean {
    return reconciliation.status === 'COMPLETED';
  }

  canCancelReconciliation(reconciliation: any): boolean {
    return reconciliation.status === 'IN_PROGRESS';
  }

  canEditReconciliation(reconciliation: any): boolean {
    return reconciliation.status === 'IN_PROGRESS';
  }

  // === PAGINACIÓN - MÉTODOS AUXILIARES ===

  getVisiblePages(): number[] {
    const pages: number[] = [];
    const maxVisible = 5;

    let start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(this.totalPages, start + maxVisible - 1);

    // Ajustar el inicio si estamos cerca del final
    if (end - start < maxVisible - 1) {
      start = Math.max(1, end - maxVisible + 1);
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    return pages;
  }

  getPaginationInfo(): string {
    const start = ((this.currentPage - 1) * this.pageSize) + 1;
    const end = Math.min(this.currentPage * this.pageSize, this.totalRecords);
    return `Mostrando ${start} - ${end} de ${this.totalRecords} conciliaciones`;
  }
}
